
import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Shield, Play, ArrowRight } from 'lucide-react';
import { Language, View } from '../types';
import { goldenSpring } from '../lib/animations';

interface AboutPageProps {
  lang: Language;
  setView: (view: View) => void;
  onBack: () => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ lang, setView }) => {
  return (
    <div className="min-h-dvh pt-28 pb-20 px-6 lg:px-12 bg-white">
      <article className="max-w-3xl mx-auto">
        {/* Hero Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={goldenSpring}
          className="mb-12"
        >
          <span className="swiss-label text-primary mb-4 block">
            {lang === 'en' ? 'ABOUT' : 'درباره من'}
          </span>
          <h1 className="swiss-headline text-5xl md:text-6xl lg:text-7xl text-brandDark mb-6">
            {lang === 'en' ? (
              <>Who is <span className="swiss-accent text-primary">Kayvan?</span></>
            ) : (
              <>کیوان <span className="swiss-accent text-primary">کیه؟</span></>
            )}
          </h1>
        </motion.header>

        {/* Featured Image */}
        <motion.figure
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...goldenSpring, delay: 0.1 }}
          className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-gray-100 shadow-2xl shadow-black/10 mb-16"
        >
          <img
            src="./kayvan.png"
            alt="Kayvan"
            className="w-full h-full object-cover object-top"
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1544168190-79c17527004f?auto=format&fit=crop&q=80&w=1000";
            }}
          />
        </motion.figure>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...goldenSpring, delay: 0.2 }}
          className="space-y-12"
        >
          {/* Lead paragraph */}
          <p className="swiss-subhead text-xl md:text-2xl text-brandDark leading-relaxed text-pretty">
            {lang === 'en'
              ? "For over a decade, I lived with a backpack on the road, exploring, and constantly stepping into the unknown. That journey wasn't just geographical; it pushed me into deeper questions."
              : "بیشتر از ده سال با یه کوله‌پشتی تو جاده‌ها زندگی کردم، گشتم و مدام پا گذاشتم تو ناشناخته‌ها. این سفر فقط جغرافیایی نبود؛ پرتم کرد سمت سوالای عمیق‌تری."}
          </p>

          {/* Quote - styled as a pull quote */}
          <blockquote className={`relative py-8 ${lang === 'fa' ? 'border-r-4 pr-8' : 'border-l-4 pl-8'} border-primary/30`}>
            <p className="swiss-accent text-primary text-xl md:text-2xl leading-relaxed text-pretty">
              {lang === 'en'
                ? "How do we stay true to ourselves while staying connected to others? Why do conflicts repeat? What does freedom look like from the inside?"
                : "چطور با وجود ارتباط با بقیه، با خودمون صادق بمونیم؟ چرا دعواها تکراری‌ان؟ آزادی از درون چه شکلیه؟"}
            </p>
          </blockquote>

          {/* Intro to methodologies */}
          <p className="swiss-body text-lg text-gray-600 text-pretty leading-relaxed">
            {lang === 'en'
              ? "Those questions led me to explore wonderful ideas from Buddhist temple wisdom to powerful modern tools that changed my life as well as my clients:"
              : "این سوالات منو به کشف ایده‌های شگفت‌انگیزی رسوند، از خرد معابد بودایی گرفته تا ابزارهای مدرن قدرتمندی که زندگی من و کلاینت‌هام رو تغییر داد:"}
          </p>

          {/* Methodologies - card style */}
          <div className="grid gap-6">
            <div className="p-6 rounded-xl bg-stone-50 border border-stone-100">
              <h4 className="flex items-center gap-3 swiss-subhead text-brandDark text-lg mb-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <MessageCircle className="w-5 h-5 text-primary" />
                </span>
                {lang === 'en' ? "Non-violent Communication (NVC)" : "ارتباط بدون خشونت (NVC)"}
              </h4>
              <p className="swiss-body text-gray-600 leading-relaxed">
                {lang === 'en'
                  ? "I trained with international schools to learn how empathy can turn conflict into connection."
                  : "من تو مدارس بین‌المللی یاد گرفتم چطور همدلی می‌تونه دعوا رو به دوستی تبدیل کنه."}
              </p>
            </div>

            <div className="p-6 rounded-xl bg-stone-50 border border-stone-100">
              <h4 className="flex items-center gap-3 swiss-subhead text-brandDark text-lg mb-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Shield className="w-5 h-5 text-primary" />
                </span>
                {lang === 'en' ? "Internal Family Systems (IFS)" : "سیستم‌های خانواده درونی (IFS)"}
              </h4>
              <p className="swiss-body text-gray-600 leading-relaxed">
                {lang === 'en'
                  ? "A modern psychological approach to recognize and reconcile with different parts of our personality, so we can transform inner conflicts into peace and achieve calm and integration under the leadership of the 'True Self'."
                  : "که یک رویکرد روانشناسی مدرن برای شناخت و آشتی با بخش‌های مختلف شخصیتمونه، تا بتونیم جنگ‌های درونیمون رو به صلح تبدیل کنیم و تحت رهبریِ «خودِ واقعی» به آرامش و یکپارچگی برسیم."}
              </p>
            </div>

            <div className="p-6 rounded-xl bg-stone-50 border border-stone-100">
              <h4 className="flex items-center gap-3 swiss-subhead text-brandDark text-lg mb-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Play className="w-5 h-5 text-primary" />
                </span>
                {lang === 'en' ? "Coaching" : "کوچینگ"}
              </h4>
              <p className="swiss-body text-gray-600 leading-relaxed">
                {lang === 'en'
                  ? "Turning awareness into action and momentum."
                  : "تبدیل آگاهی به عمل و حرکت."}
              </p>
            </div>
          </div>

          {/* Closing statement */}
          <footer className="pt-12 border-t border-gray-100">
            <p className="swiss-subhead text-brandDark text-xl md:text-2xl leading-relaxed text-pretty mb-10">
              {lang === 'en'
                ? "My role is to walk beside people who feel pulled toward something bigger — those who want to lead with love and live with courage, even when fear shows up."
                : "نقش من، همراهی با کساییه که کشش درونی به سمتِ چیزی بزرگتر حس می‌کنن؛ آدم‌هایی که می‌خوان با عشق رهبری کنن و با وجود همه‌ی ترس‌ها شجاعانه زندگی کنن."}
            </p>

            {/* CTA - at the end */}
            <button
              onClick={() => setView('approach')}
              className="group inline-flex items-center gap-3 text-primary font-semibold text-lg md:text-xl hover:text-primary/80 transition-colors"
            >
              {lang === 'en' ? "Learn more about my Coaching Approach" : "بیشتر درباره رویکرد کوچینگ من بدانید"}
              <ArrowRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-2 transition-transform" />
            </button>
          </footer>
        </motion.div>
      </article>
    </div>
  );
};

export default AboutPage;
